import {
  useEffect,
  useState,
  type ComponentType,
} from "react";

import { useNavigate } from "react-router-dom";

import { storeName } from "service1/authService";

type RemoteComponent =
  ComponentType<Record<string, unknown>>;

type UseNameReturn = {
  name: string;
  handleChange: (value: string) => void;
  handleSubmit: () => Promise<void>;
  Button: RemoteComponent | null;
  Input: RemoteComponent | null;
  loading: boolean;
  submitting: boolean;
  authToken: string;
};

export default function useName(): UseNameReturn {
  const [name, setName] =
    useState("");

  const [Button, setButton] =
    useState<RemoteComponent | null>(
      null
    );

  const [Input, setInput] =
    useState<RemoteComponent | null>(
      null
    );

  const [authToken, setAuthToken] =
    useState("");

  const [authorized, setAuthorized] =
    useState(false);

  const [validating, setValidating] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      try {
        const token =
          sessionStorage.getItem(
            "AUTH_TOKEN"
          );

        const type =
          sessionStorage.getItem(
            "AUTH_TYPE"
          );

        if (!token || !type) {
          navigate("/", {
            replace: true,
          });
          return;
        }

        setAuthToken(token);

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/amibit/fwp/validate`,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              token,
              type: Number(type),
            }),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Validation failed: ${response.status}`
          );
        }

        const data =
          await response.json();

        if (!data.success) {
          navigate("/", {
            replace: true,
          });
          return;
        }

        setAuthorized(true);

        const buttonModule =
          await import(
            "remote_app/Button"
          );

        const inputModule =
          await import(
            "inputfield/Input"
          );

        setButton(
          () =>
            buttonModule.default as RemoteComponent
        );

        setInput(
          () =>
            inputModule.default as RemoteComponent
        );

      } catch (error) {

        console.error(
          "Token validation error:",
          error
        );

        navigate("/", {
          replace: true,
        });

      } finally {

        setValidating(false);

      }
    };

    initialize();
  }, [navigate]);

  const formatName = (
    value: string
  ): string => {
    const cleaned =
      value.replace(
        /[^a-zA-Z\s]/g,
        ""
      );

    return cleaned
      .toLowerCase()
      .replace(
        /\b\w/g,
        (char) =>
          char.toUpperCase()
      );
  };

  const handleChange = (
    value: string
  ): void => {
    setName(formatName(value));
  };

  const handleSubmit =
    async (): Promise<void> => {

    if (submitting) return;

    if (!name.trim()) return;

    setSubmitting(true);

    try {

      const userId =
        sessionStorage.getItem(
          "userId"
        );

      if (!userId) {

        console.error(
          "UserId missing"
        );

        return;
      }

      const data =
        await storeName(
          {
            userId:
              Number(userId),
            name,
          },
          authToken
        );

      if (
        Number(data.statusType) ===
        1
      ) {

        sessionStorage.setItem(
          "name",
          name
        );

        setTimeout(() => {
          navigate(
            "/create-password"
          );
        }, 500);

      } else {

        console.log(
          "Failed to store name",
          data
        );

      }

    } catch (error) {

      console.error(
        "API error:",
        error
      );

    } finally {

      setSubmitting(false);

    }
  };

  return {
    name,
    handleChange,
    handleSubmit,
    Button,
    Input,
    authToken,
    submitting,
    loading:
      validating ||
      !authorized ||
      !Button ||
      !Input,
  };
}